import { useSession } from "@clerk/clerk-react";
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import { Building, ChevronDown, Document, Eye, Tag, User } from "assets/icons";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { HistoryLogType } from "shared/types/history-log";

interface HistoryLogProps {
  buildingId: string;
}

export const HistoryLog = ({ buildingId }: HistoryLogProps) => {
  const { session } = useSession();
  const [historyLogData, setHistoryLogData] = useState<HistoryLogType[]>([]);

  useEffect(() => {
    const getHistoryLogData = async () => {
      const res = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/history-log `,
        { method: "GET" },
        session,
      );

      let historyLogs = await res.json();
      setHistoryLogData(historyLogs);
    };

    getHistoryLogData();
  }, []);

  return (
    <div className="flex flex-col gap-8">
      <ul className="flex items-center gap-4 rounded-md border border-neutral-primary/20 p-2.5 text-sm font-medium">
        <li>
          <a className="flex items-center gap-1" href="#">
            Filters <ChevronDown className="h-[18px] w-[18px]" />
          </a>
        </li>
        <li>
          <a className="flex items-center gap-1" href="#">
            <Eye className="h-[18px] w-[18px]" /> Bekijk (milestones){" "}
            <ChevronDown className="h-[18px] w-[18px]" />
          </a>
        </li>
      </ul>
      <section>
        <Table
          classNames={{
            wrapper: "shadow-none rounded-none p-0",
            thead: "h-fit border-b-2 border-neutral-primary/40",
            th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
            tbody: "border-b divide-y divide-neutral-primary/15",
            tr: "divide-x divide-neutral-primary/15",
            td: "py-2.5",
          }}
        >
          <TableHeader>
            <TableColumn>Datum</TableColumn>
            <TableColumn>Unit</TableColumn>
            <TableColumn>Labels</TableColumn>
            <TableColumn>Samenvatting</TableColumn>
            <TableColumn>Tegenpartij(en)</TableColumn>
            <TableColumn>Referentie / unieke code</TableColumn>
            <TableColumn>Document</TableColumn>
            <TableColumn>Documentdatum</TableColumn>
            <TableColumn>Vervaldatum</TableColumn>
            <TableColumn>Gelogd door</TableColumn>
            <TableColumn>Gelogd op</TableColumn>
          </TableHeader>
          <TableBody>
            {historyLogData &&
              historyLogData.map((log: HistoryLogType) => (
                <TableRow>
                  <TableCell>{log.relevantDate}</TableCell>
                  <TableCell>
                    {log.unitId && (
                      <Link to={`unit/detail/${log.unitId}`}>
                        <Chip
                          className="gap-1"
                          startContent={
                            <Building className="h-3.5 w-3.5 text-moss-primary" />
                          }
                          classNames={{
                            base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                            content: "px-0 font-medium",
                          }}
                        >
                          {log.unitId}
                        </Chip>
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="bg-transparent text-moss-primary">
                      {log.labels &&
                        log.labels.map((label: string) => (
                          <Chip
                            className="gap-1"
                            startContent={
                              <Tag className="h-3.5 w-3.5 text-moss-primary" />
                            }
                            classNames={{
                              base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                              content: "px-0 font-medium",
                            }}
                          >
                            {label}
                          </Chip>
                        ))}
                    </span>
                  </TableCell>
                  <TableCell>{log.summary}</TableCell>
                  <TableCell>
                    {log.relations && log.relations.length > 0 ? (
                      log.relations.map((relation: any) => (
                        <Link
                          to={`${relation?.type}/${relation?.id}`}
                          key={relation?.id}
                        >
                          <Chip
                            className="gap-1"
                            startContent={
                              <User className="h-3.5 w-3.5 text-moss-primary" />
                            }
                            classNames={{
                              base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                              content: "px-0 font-medium",
                            }}
                          >
                            {relation.name}
                          </Chip>
                        </Link>
                      ))
                    ) : (
                      <p className="text-sm font-medium">Niet ingevuld</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="cursor-pointer bg-transparent text-moss-primary">
                      {log.referenceCode}
                    </span>
                  </TableCell>
                  <TableCell>
                    <a
                      href={log?.sharePointFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="flex gap-2">
                        Bekijk document <Document className="cursor-pointer" />
                      </span>
                    </a>
                  </TableCell>
                  <TableCell>
                    <span className="cursor-pointer bg-transparent text-moss-primary">
                      {log.documentDate as any}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="cursor-pointer bg-transparent text-moss-primary">
                      {log.expirationDate as any}
                    </span>
                  </TableCell>
                  <TableCell>{log.createdByUserFullname}</TableCell>
                  <TableCell>{log.relevantDate}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
};

export default HistoryLog;
